import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../../i18n/LanguageContext';
import { MapPinIcon } from '../../utils/IconWrapper';
import Section from '../../components/ui/Section';
import Button from '../../components/ui/Button';
import { Helmet } from 'react-helmet';

const PageContainer = styled.div`
  padding-top: 70px;
`;

const HeroSection = styled.div`
  background-image: url('https://images.unsplash.com/photo-1560448204-603b3fc33ddc?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80');
  background-size: cover;
  background-position: center;
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const HeroContent = styled.div`
  position: relative;
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: white;
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const ServiceTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: var(--accent-primary);
  }
`;

const ServiceDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: 1.5rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  
  svg {
    margin-top: 5px;
    margin-right: 1rem;
    min-width: 24px;
    color: var(--accent-primary);
  }
`;

const FeatureText = styled.div`
  h3 {
    margin-bottom: 0.5rem;
    font-size: 1.2rem;
  }
  
  p {
    color: var(--text-secondary);
    line-height: 1.6;
  }
`;

const CallToAction = styled.div`
  background-color: var(--bg-secondary);
  padding: 3rem;
  text-align: center;
  border-radius: 8px;
  margin: 3rem 0;
`;

const CTATitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const CTAText = styled.p`
  color: var(--text-secondary);
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const BuySell: React.FC = () => {
  const { language } = useLanguage();
  
  return (
    <PageContainer>
      <Helmet>
        <title>
          {language === 'it' 
            ? 'Compravendite Immobiliari | Servizi J-O-H' 
            : 'Real Estate Buy & Sell | J-O-H Services'}
        </title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Servizi professionali di compravendita immobiliare: consulenza completa per acquistare o vendere proprietà di prestigio in Toscana e Liguria.' 
            : 'Professional real estate buy & sell services: comprehensive consulting to buy or sell prestigious properties in Tuscany and Liguria.'} 
        />
      </Helmet>
      
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            {language === 'it' ? 'Compravendite Immobiliari' : 'Real Estate Buy & Sell'}
          </HeroTitle>
          <HeroSubtitle>
            {language === 'it'
              ? 'Servizi professionali per acquistare o vendere proprietà di prestigio con tranquillità e sicurezza.'
              : 'Professional services to buy or sell prestigious properties with peace of mind and security.'}
          </HeroSubtitle>
          <Button variant="primary" to="/contact">
            {language === 'it' ? 'Richiedi Informazioni' : 'Request Information'}
          </Button>
        </HeroContent>
      </HeroSection>
      
      <Section padding="6rem 0">
        <Content>
          <ServiceTitle>
            {language === 'it' ? 'I Nostri Servizi di Compravendita' : 'Our Buy & Sell Services'}
          </ServiceTitle>
          
          <ServiceDescription>
            {language === 'it'
              ? 'Che tu stia cercando di acquistare o vendere una proprietà, il nostro team di professionisti immobiliari è qui per aiutarti. Offriamo una consulenza completa, dalla valutazione iniziale alla negoziazione e alla finalizzazione della transazione, garantendo sempre i migliori risultati.'
              : 'Whether you are looking to buy or sell a property, our team of real estate professionals is here to help. We offer comprehensive consulting, from initial valuation to negotiation and transaction finalization, always ensuring the best results.'}
          </ServiceDescription>
          
          <ServiceDescription>
            {language === 'it'
              ? 'Con una profonda conoscenza del mercato immobiliare locale e un approccio personalizzato, ti guidiamo attraverso ogni fase del processo di compravendita, proteggendo i tuoi interessi e ottimizzando il valore della transazione.'
              : 'With deep knowledge of the local real estate market and a personalized approach, we guide you through every phase of the buying and selling process, protecting your interests and optimizing the value of the transaction.'}
          </ServiceDescription>
          
          <FeatureList>
            <FeatureItem>
              <MapPinIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Valutazioni Immobiliari' : 'Property Valuations'}</h3>
                <p>
                  {language === 'it'
                    ? 'Offriamo valutazioni immobiliari accurate basate su analisi di mercato approfondite, considerando tutti i fattori che influenzano il valore della proprietà.'
                    : 'We offer accurate property valuations based on in-depth market analysis, considering all factors that influence the property value.'}
                </p>
              </FeatureText>
            </FeatureItem>
            
            <FeatureItem>
              <MapPinIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Marketing Strategico' : 'Strategic Marketing'}</h3>
                <p>
                  {language === 'it'
                    ? 'Per le proprietà in vendita, sviluppiamo strategie di marketing mirate per raggiungere gli acquirenti giusti, utilizzando canali tradizionali e digitali.'
                    : 'For properties for sale, we develop targeted marketing strategies to reach the right buyers, using traditional and digital channels.'}
                </p>
              </FeatureText>
            </FeatureItem>
            
            <FeatureItem>
              <MapPinIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Ricerca Personalizzata' : 'Personalized Search'}</h3>
                <p>
                  {language === 'it'
                    ? 'Per gli acquirenti, conduciamo ricerche personalizzate basate sulle tue esigenze specifiche, presentandoti solo proprietà che corrispondono ai tuoi criteri.'
                    : 'For buyers, we conduct personalized searches based on your specific needs, presenting you only with properties that match your criteria.'}
                </p>
              </FeatureText>
            </FeatureItem>
            
            <FeatureItem>
              <MapPinIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Negoziazione e Contratti' : 'Negotiation and Contracts'}</h3>
                <p>
                  {language === 'it'
                    ? 'Gestiamo tutte le negoziazioni e la preparazione dei contratti, assicurandoci che i tuoi interessi siano sempre protetti durante l\'intero processo.'
                    : 'We handle all negotiations and contract preparation, ensuring your interests are always protected throughout the entire process.'}
                </p>
              </FeatureText>
            </FeatureItem>
          </FeatureList>
          
          <CallToAction>
            <CTATitle>
              {language === 'it' ? 'Pronto a Comprare o Vendere?' : 'Ready to Buy or Sell?'}
            </CTATitle>
            <CTAText>
              {language === 'it'
                ? 'Contattaci oggi per una consulenza personalizzata e scopri come possiamo aiutarti a raggiungere i tuoi obiettivi immobiliari.'
                : 'Contact us today for a personalized consultation and discover how we can help you achieve your real estate goals.'}
            </CTAText>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci Ora' : 'Contact Us Now'}
            </Button>
          </CallToAction>
        </Content>
      </Section>
    </PageContainer>
  );
};

export default BuySell; 
